import React, { useState, useEffect, useCallback } from 'react'
import { Alert, Card, Row, Col, Badge, Form, InputGroup } from 'react-bootstrap'
import { toast } from 'react-toastify'
import api from 'api/api'

import UbicacionSelector from 'components/UbicacionSelector'
import { LoadingSpinner } from 'components/LoadingSpinner'
import { formatNumberForDisplay, parseNumberForServer } from 'utils/numberUtils'
import { getNormalizedValue } from 'utils/ubicacionUtils'
import { requiereEnvio } from 'config/pedidos'

function EntregaTab({
	tipoPedido,
	puntoEntrega,
	onChange,
	disabled = false,
	showPaymentWarning = false,
	costoEnvioManual = false,
	onCostoEnvioManualChange
}) {
	const [areas, setAreas] = useState([])
	const [loadingAreas, setLoadingAreas] = useState(false)
	const [inicialisacionCompleta, setInicialisacionCompleta] = useState(false)
	const [costoManualInput, setCostoManualInput] = useState('')

	// Verificar si este tipo de pedido requiere envío
	const requiereEnvioActual = requiereEnvio(tipoPedido)
	
	// Inicializar el input de costo manual cuando cambia el modo o el valor
	useEffect(() => {
		if (costoEnvioManual && puntoEntrega?.costoEnvio) {
			setCostoManualInput(formatNumberForDisplay(puntoEntrega.costoEnvio.toString()))
		}
	}, [costoEnvioManual, puntoEntrega?.costoEnvio])

	// Cargar áreas 
	useEffect(() => {
		if (!requiereEnvioActual) {
			setAreas([])
			setInicialisacionCompleta(true)
			return
		}

		const cargarAreas = async () => {
			try {
				setLoadingAreas(true)
				const { datos } = await api.get('p/areas-cobertura')

				// /p/areas-cobertura devuelve { areas: [...], mapConfig: ... }
				const areasArray = Array.isArray(datos) ? datos : (datos?.areas || [])

				// Transformar datos al formato esperado por UbicacionSelector
				const areasFormateadas = areasArray.map((area, index) => ({
					id: area.id || (index + 1),
					nombre: area.nombre,
					color: area.color || '#0373c5',
					fillColor: area.fillColor || 'lightblue',
					poligono: area.poligono,
					costoBase: area.precio || 0
				}))
				setAreas(areasFormateadas)
			} catch (error) {
				console.error('Error cargando áreas de cobertura:', error)
				toast.error('Error al cargar las áreas de cobertura')
				setAreas([])
			} finally {
				setLoadingAreas(false)
				setInicialisacionCompleta(true)
			}
		}

		cargarAreas()
	}, [requiereEnvioActual])

	// Función para validar ubicación inicial
	const validarUbicacionInicial = useCallback((areasParaValidar = areas) => {
		if (
			!puntoEntrega?.lat ||
			!puntoEntrega?.lon ||
			!areasParaValidar?.length ||
			!window.google?.maps?.geometry ||
			puntoEntrega.areaId !== undefined // Solo validar si areaId es undefined (nunca validado)
		) {
			return
		}

		const punto = new window.google.maps.LatLng(puntoEntrega.lat, puntoEntrega.lon)
		let areaEncontrada = null

		for (const area of areasParaValidar) {
			const poligono = new window.google.maps.Polygon({
				paths: area.poligono.map(p => ({ lat: p.lat, lng: p.lon }))
			})
			if (window.google.maps.geometry.poly.containsLocation(punto, poligono)) {
				areaEncontrada = area
				break
			}
		}

		if (areaEncontrada) {
			const datosActualizados = {
				...puntoEntrega,
				areaId: areaEncontrada.id,
				costoEnvio: areaEncontrada.costoBase || 0,
				metodoCalculo: 'area_fija',
				detallesCalculo: `Costo fijo para ${areaEncontrada.nombre}`
			}
			onChange(datosActualizados)
		} else {
			console.log('❌ NO se encontró área para las coordenadas')
			onChange({
				...puntoEntrega,
				areaId: null,
				costoEnvio: 0,
				metodoCalculo: null,
				detallesCalculo: null
			})
		}
	}, [puntoEntrega, areas, onChange])

	// Listener para cuando Google Maps esté listo
	useEffect(() => {
		const handleGoogleMapsLoad = (event) => {
			validarUbicacionInicial()
		}

		// Si Google Maps ya está cargado, validar inmediatamente
		if (window.google?.maps?.geometry && areas.length > 0) {
			validarUbicacionInicial()
		} else {
			// Si no, escuchar el evento
			window.addEventListener('google-maps-loaded', handleGoogleMapsLoad)
		}

		return () => {
			window.removeEventListener('google-maps-loaded', handleGoogleMapsLoad)
		}
	}, [validarUbicacionInicial, areas])

	// Validar cuando cambien las áreas (para el caso de que Google Maps ya esté listo)
	useEffect(() => {
		if (areas.length > 0 && window.google?.maps?.geometry) {
			validarUbicacionInicial()
		}
	}, [areas, validarUbicacionInicial])

	// Calcular costo cuando cambia el área
	const calcularCostoEnvio = useCallback(
		(areaId) => {
			if (!areaId || !areas.length) return
			// No recalcular si el modo manual está activo
			if (costoEnvioManual) return

			const area = areas.find(a => a.id === areaId)
			if (!area) {
				console.warn('Área no encontrada:', areaId)
				return
			}

			// Solo actualizar si el costo o detalles han cambiado
			if (
				puntoEntrega?.areaId !== areaId ||
				puntoEntrega?.costoEnvio !== area.costoBase ||
				puntoEntrega?.detallesCalculo !== `Costo fijo para ${area.nombre}`
			) {
				onChange({
					...puntoEntrega,
					areaId: area.id,
					costoEnvio: area.costoBase || 0,
					metodoCalculo: 'area_fija',
					detallesCalculo: `Costo fijo para ${area.nombre}`
				})
			}
		},
		[areas, puntoEntrega, onChange, costoEnvioManual]
	)

	// Efecto para calcular costo cuando cambia areaId (solo si no es modo manual)
	useEffect(() => {
		if (requiereEnvioActual && puntoEntrega?.areaId && areas.length > 0 && !costoEnvioManual) {
			calcularCostoEnvio(puntoEntrega.areaId)
		}
	}, [puntoEntrega?.areaId, requiereEnvioActual, areas, calcularCostoEnvio, costoEnvioManual])

	const handleUbicacionChange = useCallback(
		(nuevaUbicacion) => {
			// Si se limpia la ubicación
			if (!nuevaUbicacion) {
				onChange(null)
				return
			}

			// Evitar actualizaciones redundantes - SOLO comparar campos que el usuario modifica
			const datosImportantesCambiaron =
				nuevaUbicacion.lat !== puntoEntrega?.lat ||
				nuevaUbicacion.lon !== puntoEntrega?.lon ||
				nuevaUbicacion.direccion !== puntoEntrega?.direccion ||
				nuevaUbicacion.barrio !== puntoEntrega?.barrio ||
				nuevaUbicacion.ciudad !== puntoEntrega?.ciudad ||
				nuevaUbicacion.observaciones !== puntoEntrega?.observaciones ||
				nuevaUbicacion.areaId !== puntoEntrega?.areaId

			if (!datosImportantesCambiaron) {
				return
			}

			// Crear nuevo punto de entrega
			const nuevoPuntoEntrega = {
				...puntoEntrega,
				...nuevaUbicacion
			}

			onChange(nuevoPuntoEntrega)
		},
		[puntoEntrega, onChange]
	)

	const formatearPrecio = (precio) => {
		if (!precio || precio === 0) return '₲ 0'
		return `₲ ${formatNumberForDisplay(precio.toString())}`
	}

	const obtenerNombreArea = (areaId) => {
		const area = areas.find(a => a.id === areaId)
		return area?.nombre || `Área ${areaId}`
	}

	// Si el tipo de pedido no requiere envío
	if (!requiereEnvioActual) {
		return (
			<div className="entrega-tab">
				<Alert variant="info">
					<i className="fa fa-info-circle me-2"></i>
					<strong>Este tipo de pedido no requiere configuración de entrega.</strong>
					<br />
					<small>Los pedidos de tipo "{tipoPedido}" no necesitan punto de entrega.</small>
				</Alert>
			</div>
		)
	}

	// Si está cargando las áreas
	if (loadingAreas) {
		return (
			<div className="entrega-tab">
				<div className="text-center py-4">
					<LoadingSpinner />
					<p className="mt-3 text-muted">Cargando áreas de cobertura...</p>
				</div>
			</div>
		)
	}

	return (
		<div className="entrega-tab">
			{showPaymentWarning && (
				<Alert variant="warning" className="mb-4">
					<i className="fa fa-exclamation-triangle me-2"></i>
					<strong>Pedido ya pagado:</strong> Los cambios en la entrega pueden afectar el monto total.
					Verifique que el nuevo total sea correcto antes de guardar.
				</Alert>
			)}

			{areas.length > 0 && (
				<Alert variant="info" className="mb-4">
					<i className="fa fa-map me-2"></i>
					<strong>Áreas de cobertura disponibles:</strong>
					<div className="mt-2">
						{areas.map(area => (
							<Badge
								key={area.id}
								bg="secondary"
								className="me-2 mb-1"
								style={{ backgroundColor: area.color }}
							>
								{area.nombre}
							</Badge>
						))}
					</div>
				</Alert>
			)}

			<Card className="mb-4">
				<Card.Header>
					<h6 className="mb-0">
						<i className="fa fa-map-marker-alt me-2"></i>
						Punto de Entrega
					</h6>
				</Card.Header>
				<Card.Body>
					{inicialisacionCompleta ? (
						<UbicacionSelector
							ubicacion={puntoEntrega}
							onUbicacionChange={handleUbicacionChange}
							areas={areas}
							disabled={disabled}
							required={true}
							mapHeight="350px"
							titulo="Seleccionar dirección de entrega"
							direccionLabel="Dirección de entrega"
							fueraDeAreaError="La dirección está fuera de nuestras áreas de cobertura"
							noSeleccionadaMsg="Seleccione la dirección donde se debe entregar el pedido"
							direccionPlaceholder="Ej: Av. Mariscal López 1234"
							barrioPlaceholder="Ej: Villa Morra"
							ciudadPlaceholder="Ej: Asunción"
						/>
					) : (
						<div className="text-center py-4">
							<LoadingSpinner />
							<p className="mt-3 text-muted">Cargando selector de ubicación...</p>
						</div>
					)}
				</Card.Body>
			</Card>

			{puntoEntrega?.areaId && (
				<Card className="mb-4">
					<Card.Header>
						<h6 className="mb-0">
							<i className="fa fa-calculator me-2"></i>
							Costo de Envío
						</h6>
					</Card.Header>
					<Card.Body>
						{/* Toggle para costo manual */}
						{onCostoEnvioManualChange && (
							<div className="mb-3 pb-3 border-bottom">
								<Form.Check
									type="switch"
									id="costo-envio-manual-switch"
									label={
										<span>
											<strong>Costo de envío manual</strong>
											<small className="d-block text-muted">
												Activar para ingresar un costo diferente al calculado por área
											</small>
										</span>
									}
									checked={costoEnvioManual}
									onChange={(e) => {
										const esManual = e.target.checked
										if (esManual) {
											onCostoEnvioManualChange(true, puntoEntrega?.costoEnvio || 0)
											setCostoManualInput(formatNumberForDisplay((puntoEntrega?.costoEnvio || 0).toString()))
										} else {
											onCostoEnvioManualChange(false, null)
											setCostoManualInput('')
										}
									}}
									disabled={disabled}
								/>
							</div>
						)}

						{costoEnvioManual ? (
							<Row className="align-items-center">
								<Col sm={8}>
									<Form.Group>
										<Form.Label>
											<strong>Costo de envío personalizado</strong>
										</Form.Label>
										<InputGroup>
											<InputGroup.Text>₲</InputGroup.Text>
											<Form.Control
												type="text"
												value={costoManualInput}
												onChange={(e) => {
													const valor = e.target.value
													setCostoManualInput(valor)
													const costoNumerico = parseNumberForServer(valor) || 0
													onChange({
														...puntoEntrega,
														costoEnvio: costoNumerico
													})
												}}
												placeholder="0"
												disabled={disabled}
											/>
										</InputGroup>
										<Form.Text className="text-muted">
											Este valor reemplaza el costo calculado automáticamente
										</Form.Text>
									</Form.Group>
								</Col>
								<Col sm={4} className="text-end">
									<Badge bg="warning" className="p-2">
										<i className="fa fa-edit me-1"></i>
										Manual
									</Badge>
								</Col>
							</Row>
						) : (
							<>
								<Row className="align-items-center">
									<Col sm={8}>
										<div>
											<strong>Área de entrega:</strong> {obtenerNombreArea(puntoEntrega.areaId)}
											<br />
											<small className="text-muted">{puntoEntrega.detallesCalculo}</small>
										</div>
									</Col>
									<Col sm={4} className="text-end">
										<div>
											<h5 className="text-primary mb-0">{formatearPrecio(puntoEntrega.costoEnvio || 0)}</h5>
										</div>
									</Col>
								</Row>

								{puntoEntrega.detallesCalculo && (
									<div className="mt-3 p-2 bg-light rounded">
										<small className="text-muted">
											<strong>Detalles:</strong> {puntoEntrega.detallesCalculo}
										</small>
									</div>
								)}
							</>
						)}
					</Card.Body>
				</Card>
			)}


			{puntoEntrega?.direccion && (
				<Card>
					<Card.Header>
						<h6 className="mb-0">
							<i className="fa fa-check-circle me-2"></i>
							Resumen de Entrega
						</h6>
					</Card.Header>
					<Card.Body>
						<div className="mb-2">
							<strong>Dirección:</strong> {getNormalizedValue(puntoEntrega.direccion)}
						</div>
						{puntoEntrega.barrio && (
							<div className="mb-2">
								<strong>Barrio:</strong> {getNormalizedValue(puntoEntrega.barrio)}
							</div>
						)}
						{puntoEntrega.ciudad && (
							<div className="mb-2">
								<strong>Ciudad:</strong> {getNormalizedValue(puntoEntrega.ciudad)}
							</div>
						)}
						{puntoEntrega.observaciones && (
							<div className="mb-2">
								<strong>Observaciones:</strong> {getNormalizedValue(puntoEntrega.observaciones)}
							</div>
						)}
						<div className="border-top pt-2 mt-2">
							<Row>
								<Col>
									<strong>Costo de envío:</strong>
								</Col>
								<Col className="text-end">
									<strong className="text-primary">{formatearPrecio(puntoEntrega.costoEnvio || 0)}</strong>
								</Col>
							</Row>
						</div>
					</Card.Body>
				</Card>
			)}
		</div>
	)
}

export default EntregaTab